<template>
  <b-card v-if="showSection" class="invoice-versions-section mt-2" no-body>
    <b-card-header class="py-1 px-2 d-flex align-items-center justify-content-between flex-wrap" style="gap: 0.5rem;">
      <div class="d-flex align-items-center">
        <feather-icon icon="LayersIcon" size="14" class="text-primary mr-50" />
        <span class="small font-weight-bold text-uppercase text-muted">Invoice versions</span>
        <b-badge v-if="revisionCount" variant="light-info" class="ml-50">{{ revisionCountLabel }}</b-badge>
      </div>
      <b-button-group v-if="canCompare" size="sm">
        <b-button
          :variant="activeTab === 'original' ? 'primary' : 'outline-primary'"
          @click="activeTab = 'original'"
        >
          Old
        </b-button>
        <b-button
          :variant="activeTab === 'revised' ? 'primary' : 'outline-primary'"
          @click="activeTab = 'revised'"
        >
          Current
        </b-button>
        <b-button
          v-if="canSideBySide"
          :variant="activeTab === 'sideBySide' ? 'primary' : 'outline-primary'"
          @click="activeTab = 'sideBySide'"
        >
          Side by side
        </b-button>
      </b-button-group>
    </b-card-header>

    <b-card-body class="p-2">
      <b-alert v-if="!revisionHistoryLoaded && loadingPreview" variant="light" show class="small py-1 mb-2">
        <b-spinner small class="mr-50" /> Loading version documents…
      </b-alert>

      <!-- Current / Old downloads (current keeps live filename pattern) -->
      <div
        v-if="showDownloadBar"
        class="d-flex flex-wrap align-items-center mb-2"
        style="gap: 0.5rem;"
      >
        <b-button
          v-if="versionFiles.hasCurrent || pendingRevisedFile"
          size="sm"
          variant="outline-primary"
          :disabled="downloadingVersion === 'current'"
          @click="downloadVersion('current')"
        >
          <b-spinner v-if="downloadingVersion === 'current'" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="12" class="mr-25" />
          Download current
        </b-button>
        <b-button
          v-if="versionFiles.hasOld"
          size="sm"
          variant="outline-secondary"
          :disabled="downloadingVersion === 'old'"
          @click="downloadVersion('old')"
        >
          <b-spinner v-if="downloadingVersion === 'old'" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="12" class="mr-25" />
          Download old
        </b-button>
        <small v-if="downloadHint" class="text-muted">{{ downloadHint }}</small>
      </div>

      <!-- Single or side-by-side preview -->
      <div v-if="activeTab === 'sideBySide' && canSideBySide" class="invoice-versions-compare mb-2">
        <b-row>
          <b-col cols="12" md="6" class="mb-1 mb-md-0">
            <div class="invoice-versions-pane-label small font-weight-bold text-muted mb-50">
              {{ originalPaneLabel }}
            </div>
            <div class="invoice-versions-pane">
              <iframe
                v-if="originalPreviewUrl"
                :src="originalPreviewUrl"
                class="invoice-versions-iframe"
                title="Previous invoice PDF"
              />
              <div v-else class="invoice-versions-placeholder text-muted small">
                Previous PDF not available yet. Backend should archive the prior file
                as <code>previous_pdf</code> / <code>original_invoice_file_path</code> on revise.
              </div>
            </div>
          </b-col>
          <b-col cols="12" md="6">
            <div class="invoice-versions-pane-label small font-weight-bold text-muted mb-50">
              {{ revisedPaneLabel }}
            </div>
            <div class="invoice-versions-pane">
              <iframe
                v-if="revisedPreviewUrl"
                :src="revisedPreviewUrl"
                class="invoice-versions-iframe"
                title="Current invoice PDF"
              />
              <div v-else class="invoice-versions-placeholder text-muted small">
                {{ revisedPlaceholder }}
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <div v-else class="invoice-versions-single mb-2">
        <div class="invoice-versions-pane-label small font-weight-bold text-muted mb-50">
          {{ activeTab === 'original' ? originalPaneLabel : revisedPaneLabel }}
        </div>
        <div class="invoice-versions-pane">
          <iframe
            v-if="activePreviewUrl"
            :src="activePreviewUrl"
            class="invoice-versions-iframe"
            :title="activeTab === 'original' ? 'Previous invoice PDF' : 'Current invoice PDF'"
          />
          <div v-else class="invoice-versions-placeholder text-muted small">
            {{ activeTab === 'original' ? 'No previous invoice file on record.' : revisedPlaceholder }}
          </div>
        </div>
      </div>

      <!-- Version list -->
      <div v-if="revisionRows.length" class="invoice-versions-list">
        <div class="small font-weight-bold text-muted mb-1">Revision history</div>
        <ul class="list-unstyled mb-0">
          <li
            v-for="row in revisionRows"
            :key="row.id || row.revision_number"
            class="invoice-versions-list-item py-1 border-bottom"
          >
            <div class="d-flex flex-wrap align-items-start justify-content-between">
              <div class="min-w-0 pr-2">
                <div class="small font-weight-medium">{{ row.label }}</div>
                <small class="text-muted">{{ row.snapshot }}</small>
              </div>
              <div class="d-flex flex-wrap flex-shrink-0" style="gap: 0.35rem;">
                <b-button
                  v-if="row.canView"
                  size="sm"
                  variant="outline-primary"
                  class="py-0 px-1"
                  @click="viewRevisionRow(row)"
                >
                  View
                </b-button>
                <b-button
                  v-if="row.pdfPath"
                  size="sm"
                  variant="outline-secondary"
                  class="py-0 px-1"
                  @click="openDocument(row.pdfPath)"
                >
                  PDF
                </b-button>
                <b-button
                  v-for="(link, idx) in row.extraLinks"
                  :key="idx"
                  size="sm"
                  variant="outline-primary"
                  class="py-0 px-1"
                  @click="openDocument(link.path)"
                >
                  <feather-icon icon="FileTextIcon" size="12" class="mr-25" />
                  {{ link.shortLabel }}
                </b-button>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <p v-else-if="pendingRevisedFile" class="text-muted small mb-0">
        New document selected — save to mark previous file as old and keep the same invoice id.
      </p>
      <b-alert
        v-else-if="showRevisionHistoryGap"
        variant="warning"
        show
        class="small py-1 mb-0"
      >
        <template v-if="revisionHistorySource === 'missing-route'">
          Revision history API is missing
          (<code>GET /invoice/:id/revision/history</code> → 404).
          Status can still be Revised; this panel will not invent log rows.
          Backend must INSERT into <code>invoice_revisions</code> on Revised PUT and expose that route.
        </template>
        <template v-else>
          No earlier versions stored. Invoices revised before version archive cannot show the old file.
          New revises snapshot the previous PDF and invoice fields into
          <code>invoice_revisions</code> / <code>metadata.revisions</code>.
        </template>
      </b-alert>
      <p v-else-if="isRevisedStatus && !versionFiles.hasOld" class="text-muted small mb-0">
        Current file is available when present on the invoice. Old/previous file appears after
        backend stores <code>previous_pdf</code> / revision snapshot paths.
      </p>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BButtonGroup,
  BBadge,
  BAlert,
  BSpinner,
  BRow,
  BCol,
} from 'bootstrap-vue'
import {
  loadInvoiceFileDataUrl,
  revisionDocumentLinks,
  formatRevisionSnapshotLabel,
  resolveInvoiceVersionFiles,
  buildVersionDownloadFilename,
  downloadInvoiceFile,
} from '@/utils/invoice-file-loader'
import {
  collectRevisionSnapshots,
  formatRevisionRowMeta,
  formatRevisionTimesLabel,
  getInvoiceRevisionCount,
} from '@/utils/invoice-revision-count'

export default {
  name: 'InvoiceVersionsSection',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BButtonGroup,
    BBadge,
    BAlert,
    BSpinner,
    BRow,
    BCol,
  },
  props: {
    invoiceId: { type: [String, Number], default: '' },
    invoice: { type: Object, default: null },
    invoiceStatus: { type: String, default: '' },
    existingFilePath: { type: String, default: null },
    existingFileDataUrl: { type: String, default: null },
    pendingRevisedFile: { type: File, default: null },
    revisionHistory: { type: Array, default: () => [] },
    revisionHistoryLoaded: { type: Boolean, default: false },
    /** From getRevisionHistory: revision-history-api | missing-route | update-response | empty | error */
    revisionHistorySource: { type: String, default: null },
    historyModalId: { type: String, default: 'modal-invoice-revision-history-detail' },
  },
  data() {
    return {
      activeTab: 'revised',
      loadingPreview: false,
      originalPreviewUrl: null,
      revisedPreviewUrl: null,
      pendingRevisedObjectUrl: null,
      downloadingVersion: null,
    }
  },
  computed: {
    isRevisedStatus() {
      return String(this.invoiceStatus || '').trim().toLowerCase() === 'revised'
    },
    invoiceForNames() {
      const inv = this.invoice && typeof this.invoice === 'object' ? { ...this.invoice } : {}
      if (!inv.invoice_id && this.invoiceId) inv.invoice_id = this.invoiceId
      if (!inv.status && this.invoiceStatus) inv.status = this.invoiceStatus
      if (!inv.file && this.existingFilePath) inv.file = this.existingFilePath
      return inv
    },
    versionFiles() {
      const resolved = resolveInvoiceVersionFiles(this.invoiceForNames, this.revisionHistory)
      // Prefer explicit existing path as current when probe found nothing
      if (!resolved.currentPath && this.existingFilePath) {
        return {
          ...resolved,
          currentPath: this.existingFilePath,
          hasCurrent: true,
          hasOld: resolved.oldPath && resolved.oldPath !== this.existingFilePath,
        }
      }
      return resolved
    },
    showSection() {
      if (this.pendingRevisedFile instanceof File) return true
      if (this.isRevisedStatus) return true
      return this.revisionHistory.length > 0 || this.versionFiles.hasOld
    },
    showDownloadBar() {
      return !!(this.versionFiles.hasCurrent || this.versionFiles.hasOld || this.pendingRevisedFile)
    },
    downloadHint() {
      if (this.versionFiles.hasCurrent && this.versionFiles.hasOld) {
        return 'Current uses the live filename; old downloads as “… (Previous).pdf”.'
      }
      if (this.pendingRevisedFile instanceof File && !this.versionFiles.hasOld) {
        return 'After save, backend should keep previous file as old.'
      }
      return ''
    },
    revisionCount() {
      return getInvoiceRevisionCount(this.invoiceForNames, this.revisionHistory)
    },
    revisionCountLabel() {
      return formatRevisionTimesLabel(this.revisionCount) || `${this.revisionCount} revisions`
    },
    showRevisionHistoryGap() {
      if (!this.revisionHistoryLoaded) return false
      if (this.revisionRows.length) return false
      if (this.revisionHistorySource === 'missing-route') return true
      return this.isRevisedStatus
    },
    sortedRevisions() {
      return [...this.revisionHistory].sort((a, b) => {
        const na = Number(a.revision_number)
        const nb = Number(b.revision_number)
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
        const ta = Date.parse(a.created_at || a.createdAt || '') || 0
        const tb = Date.parse(b.created_at || b.createdAt || '') || 0
        if (ta !== tb) return ta - tb
        return (Number(a.id) || 0) - (Number(b.id) || 0)
      })
    },
    latestRevision() {
      const rows = this.sortedRevisions
      return rows.length ? rows[rows.length - 1] : null
    },
    originalPaneLabel() {
      if (this.versionFiles.hasOld) return 'Old / Previous version'
      if (this.revisionCount) return 'Old / Previous (snapshot pending backend)'
      return 'Previous invoice (on file)'
    },
    revisedPaneLabel() {
      if (this.pendingRevisedFile instanceof File) return 'Current / New (pending save)'
      if (this.latestRevision) {
        const num = this.latestRevision.revision_number
        return num ? `Current / New (revision #${num})` : 'Current / New'
      }
      if (this.isRevisedStatus) return 'Current / New (live file)'
      return 'Current document'
    },
    revisedPlaceholder() {
      if (this.pendingRevisedFile instanceof File) return 'Preview loads after file selection.'
      return 'Upload a new invoice document or wait for backend to store the current file path.'
    },
    canCompare() {
      return !!(this.originalPreviewUrl || this.existingFilePath || this.versionFiles.hasOld)
        && !!(this.revisedPreviewUrl || this.pendingRevisedFile || this.versionFiles.hasCurrent || this.existingFilePath)
    },
    canSideBySide() {
      return !!(this.originalPreviewUrl && this.revisedPreviewUrl)
    },
    activePreviewUrl() {
      if (this.activeTab === 'original') return this.originalPreviewUrl
      return this.revisedPreviewUrl
    },
    revisionRows() {
      const merged = collectRevisionSnapshots(this.invoiceForNames, this.sortedRevisions)
      return merged.map((row) => {
        const historyMatch = this.sortedRevisions.find((rev) => (
          (rev.id != null && rev.id === row.id)
          || (rev.revision_number != null && rev.revision_number === row.revision_number)
        )) || row
        const links = revisionDocumentLinks(historyMatch).map((link) => ({
          ...link,
          shortLabel: link.kind === 'revised' ? 'New PDF'
            : link.kind === 'r1' ? 'R1'
              : link.kind === 'note' ? 'Note'
                : 'Old PDF',
        }))
        const extraLinks = links.filter((l) => l.kind === 'r1' || l.kind === 'note')
        const pdfPath = row.pdf_path
          || (links.find((l) => l.kind === 'original' || l.kind === 'revised') || {}).path
          || null
        return {
          id: row.id,
          revision_number: row.revision_number,
          label: row.revision_number != null && row.revision_number !== '' ? `Rev ${row.revision_number}` : 'Revision',
          snapshot: formatRevisionSnapshotLabel(historyMatch) || formatRevisionRowMeta(row),
          pdfPath,
          canView: !!(row.snapshot || pdfPath),
          extraLinks,
        }
      })
    },
  },
  watch: {
    existingFilePath: { handler: 'refreshPreviews', immediate: true },
    existingFileDataUrl: { handler: 'refreshPreviews' },
    pendingRevisedFile: { handler: 'refreshPreviews' },
    revisionHistory: { handler: 'refreshPreviews', deep: true },
    invoice: { handler: 'refreshPreviews', deep: true },
  },
  beforeDestroy() {
    this.revokePendingObjectUrl()
  },
  methods: {
    revokePendingObjectUrl() {
      if (this.pendingRevisedObjectUrl) {
        URL.revokeObjectURL(this.pendingRevisedObjectUrl)
        this.pendingRevisedObjectUrl = null
      }
    },
    async refreshPreviews() {
      this.loadingPreview = true
      try {
        this.revokePendingObjectUrl()
        const versions = this.versionFiles

        if (versions.oldPath) {
          this.originalPreviewUrl = await loadInvoiceFileDataUrl(versions.oldPath)
        } else if (this.existingFileDataUrl && !versions.hasCurrent) {
          this.originalPreviewUrl = this.existingFileDataUrl
        } else {
          this.originalPreviewUrl = null
        }

        if (this.pendingRevisedFile instanceof File) {
          this.pendingRevisedObjectUrl = URL.createObjectURL(this.pendingRevisedFile)
          this.revisedPreviewUrl = this.pendingRevisedObjectUrl
        } else if (versions.currentPath) {
          this.revisedPreviewUrl = await loadInvoiceFileDataUrl(versions.currentPath)
        } else if (this.isRevisedStatus && this.existingFileDataUrl) {
          this.revisedPreviewUrl = this.existingFileDataUrl
        } else {
          this.revisedPreviewUrl = null
        }

        if (this.canSideBySide && this.activeTab !== 'sideBySide') {
          this.activeTab = 'revised'
        }
      } finally {
        this.loadingPreview = false
      }
    },
    viewRevisionRow(row) {
      if (row && row.pdfPath) {
        this.openDocument(row.pdfPath)
        return
      }
      if (this.$bvModal && this.historyModalId) this.$bvModal.show(this.historyModalId)
    },
    async openDocument(path) {
      const url = await loadInvoiceFileDataUrl(path)
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    },
    async downloadVersion(version) {
      this.downloadingVersion = version
      try {
        if (version === 'current' && this.pendingRevisedFile instanceof File) {
          const url = URL.createObjectURL(this.pendingRevisedFile)
          const link = document.createElement('a')
          link.href = url
          link.download = buildVersionDownloadFilename(this.invoiceForNames, 'current')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          return
        }

        const path = version === 'old'
          ? this.versionFiles.oldPath
          : this.versionFiles.currentPath
        if (!path) return

        const filename = buildVersionDownloadFilename(
          this.invoiceForNames,
          version === 'old' ? 'old' : 'current',
        )
        await downloadInvoiceFile(path, filename)
      } finally {
        this.downloadingVersion = null
      }
    },
  },
}
</script>

<style scoped>
.invoice-versions-pane {
  border: 1px solid #ebe9f1;
  border-radius: 0.35rem;
  background: #f8f8f8;
  min-height: 280px;
  overflow: hidden;
}

.invoice-versions-iframe {
  width: 100%;
  height: 420px;
  border: 0;
  display: block;
  background: #fff;
}

.invoice-versions-placeholder {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
}

.invoice-versions-list-item:last-child {
  border-bottom: 0 !important;
}
</style>
